import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';

/**
 * BottomSheet — a mobile-first drawer that slides up from the bottom.
 *
 * Designed to match a premium grocery-delivery sheet: dimmed backdrop,
 * rounded top corners, spring-based motion. On desktop it centers as a
 * compact card.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - dismissible: when false the sheet ignores backdrop taps, Escape and
 *    swipe-down (used for the mandatory address selector).
 *  - heightClass: tailwind height utility for the mobile sheet body.
 *  - showHandle: render the drag handle affordance.
 */
export default function BottomSheet({
  open,
  onClose,
  dismissible = true,
  heightClass = 'h-[60vh]',
  showHandle = true,
  children,
  labelledBy
}) {
  // Lock body scroll while open.
  useEffect(() => {
    if (!open) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [open]);

  // Escape closes only when dismissible.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && dismissible) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dismissible, onClose]);

  const handleBackdrop = () => { if (dismissible) onClose?.(); };

  const handleDragEnd = (_e, info) => {
    if (dismissible && info.offset.y > 120) onClose?.();
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          aria-modal="true"
          role="dialog"
          aria-labelledby={labelledBy}
        >
          {/* Dimmed backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
            onClick={handleBackdrop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Sheet */}
          <motion.div
            className={`relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col ${heightClass} sm:h-auto sm:max-h-[85vh] sm:mb-0 overflow-hidden`}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 340 }}
            drag={dismissible ? 'y' : false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={handleDragEnd}
          >
            {showHandle && (
              <div className="pt-3 pb-1 flex justify-center shrink-0">
                <span className="h-1.5 w-11 rounded-full bg-gray-300" />
              </div>
            )}
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
